// frontend/src/components/client/mobile/MobileFuelRequests.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, Trash2, Fuel, Calendar, X, RefreshCw, ChevronRight,
  Navigation, MapPin, Eye, ExternalLink, Cloud, AlertCircle,
  Send, CheckCircle, Clock, Shield, ListChecks, Download,
  Users, Luggage, Moon, Wifi, ArrowRight
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { getFuelRequests, createFuelRequest, cancelFuelRequest, getMyAircraft } from '../../../services/clientApi';
import FuelRequirementCalculator from '../FuelRequirementCalculator';
import FlightPlanPDF from '../FlightPlanPDF';
import useDistanceCalculator from '../../../hooks/useDistanceCalculator';

// ─── Styles et constantes ──────────────────────────────────────────────────────
const STATUS = {
  pending:   { label: 'Pending', bg: 'rgba(234,179,8,0.15)',  color: '#eab308' },
  approved:  { label: 'Approved', bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  completed: { label: 'Completed', bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' },
  rejected:  { label: 'Rejected', bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
  cancelled: { label: 'Cancelled', bg: 'rgba(100,116,139,0.15)', color: '#64748b' },
};

const Badge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: s.bg, color: s.color }}>{s.label}</span>;
};

// ─── Bottom Sheet ──────────────────────────────────────────────────────────────
const BottomSheet = ({ open, onClose, title, children }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#1e293b', borderRadius: '24px 24px 0 0',
        maxHeight: '92dvh', display: 'flex', flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom)',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>{title}</span>
          <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={15} color="#94a3b8" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
};

// ─── Drawer (détail de la demande) ────────────────────────────────────────────
const FuelRequestDrawerMobile = ({ request, aircraft, onClose, onRefresh }) => {
  const [flightPlan, setFlightPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fuel');
  const [departureIcao, setDepartureIcao] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('aeroTrick');

  // Récupérer home base
  useEffect(() => {
    const fetchHomeBase = async () => {
      try {
        const token = localStorage.getItem('client_access_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/profile/home-base`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setDepartureIcao(data.home_base || 'K5C1');
      } catch (err) {
        setDepartureIcao('K5C1');
      }
    };
    fetchHomeBase();
  }, []);

  // Récupérer infos compagnie
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const token = localStorage.getItem('client_access_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/company/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.logo_url) setCompanyLogo(data.logo_url);
          if (data.name) setCompanyName(data.name);
        }
      } catch (err) {}
    };
    fetchCompanyInfo();
  }, []);

  useEffect(() => {
    loadFlightPlan();
  }, [request.id]);

  const loadFlightPlan = async () => {
    try {
      const token = localStorage.getItem('client_access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${request.id}/flight-plan`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setFlightPlan(data);
    } catch (err) {
      console.error('Error loading flight plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#eab308';
      case 'approved': return '#22c55e';
      case 'completed': return '#3b82f6';
      case 'cancelled': return '#ef4444';
      default: return '#64748b';
    }
  };

  const destinationIcao = request.destination_icao || null;

  const StatusTimeline = () => {
    const steps = [
      { key: 'pending', label: 'Requested', icon: Send },
      { key: 'approved', label: 'Approved', icon: CheckCircle },
      { key: 'completed', label: 'Fueled', icon: Fuel }
    ];
    const currentIndex = steps.findIndex(s => s.key === request.status);
    
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isComplete = idx <= currentIndex;
            const isCurrent = idx === currentIndex;
            return (
              <div key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 22,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isComplete ? 'rgba(34,197,94,0.2)' : '#334155',
                  border: isCurrent ? `2px solid ${getStatusColor(request.status)}` : 'none',
                }}>
                  <Icon size={20} color={isComplete ? '#22c55e' : '#64748b'} />
                </div>
                <span style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>{step.label}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569' }}>
          <span>{request.quantity} gal requested</span>
          {request.approved_quantity && <span>{request.approved_quantity} gal approved</span>}
        </div>
      </div>
    );
  };

  const SkyVectorLinks = () => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ background: '#0f172a', borderRadius: 16, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: 10, color: '#64748b' }}>Departure</p>
            <p style={{ fontSize: 16, fontWeight: 'bold', color: '#fff' }}>{departureIcao || '—'}</p>
          </div>
          <ArrowRight size={16} color="#475569" />
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: 10, color: '#64748b' }}>Destination</p>
            <p style={{ fontSize: 16, fontWeight: 'bold', color: '#fff' }}>{destinationIcao || '—'}</p>
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {destinationIcao && (
          <>
            <a href={`https://skyvector.com/airport/${destinationIcao}`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(59,130,246,0.15)', padding: '8px 12px', borderRadius: 20, fontSize: 11, color: '#60a5fa', textDecoration: 'none' }}>
              <Eye size={12} style={{ display: 'inline', marginRight: 4 }} /> View Airport
            </a>
            {departureIcao && (
              <a href={`https://skyvector.com/?fpl=${departureIcao}%20${destinationIcao}`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(34,197,94,0.15)', padding: '8px 12px', borderRadius: 20, fontSize: 11, color: '#4ade80', textDecoration: 'none' }}>
                <Navigation size={12} style={{ display: 'inline', marginRight: 4 }} /> Plan Route
              </a>
            )}
            <a href={`https://www.aviationweather.gov/metar/data?ids=${destinationIcao}&format=raw`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(6,182,212,0.15)', padding: '8px 12px', borderRadius: 20, fontSize: 11, color: '#22d3ee', textDecoration: 'none' }}>
              <Cloud size={12} style={{ display: 'inline', marginRight: 4 }} /> METAR/TAF
            </a>
            <a href={`https://www.notams.faa.gov/dinsQueryWeb/queryRetrievalMapAction.do?reportType=RAW&retrieveLocId=${destinationIcao}`} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(239,68,68,0.15)', padding: '8px 12px', borderRadius: 20, fontSize: 11, color: '#f87171', textDecoration: 'none' }}>
              <AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} /> NOTAMs
            </a>
          </>
        )}
      </div>
    </div>
  );

  const FAAChecklist = () => {
    const [items, setItems] = useState(flightPlan?.checklist_items || {
      preflight_inspection_complete: false, documents_on_board: false, notam_checked: false,
      weather_briefing_complete: false, weight_and_balance_computed: false,
      fuel_requirements_computed: false, alternate_airport_identified: false,
      ifr_clearance_obtained: false, flight_plan_filed: false
    });
    const [saving, setSaving] = useState(false);

    const checklistLabels = {
      preflight_inspection_complete: 'Preflight inspection (14 CFR §91.103)',
      documents_on_board: 'Documents on board (AROW)',
      notam_checked: 'NOTAMs checked',
      weather_briefing_complete: 'Weather briefing obtained',
      weight_and_balance_computed: 'Weight & balance computed',
      fuel_requirements_computed: 'Fuel requirements (FAA §91.151/§91.169)',
      alternate_airport_identified: 'Alternate airport identified',
      ifr_clearance_obtained: 'IFR clearance obtained',
      flight_plan_filed: 'Flight plan filed'
    };

    const handleToggle = async (key) => {
      const newItems = { ...items, [key]: !items[key] };
      setItems(newItems);
      setSaving(true);
      try {
        const token = localStorage.getItem('client_access_token');
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/client/fuel/${request.id}/checklist`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ checklist_items: newItems })
        });
      } catch (err) {}
      finally { setSaving(false); }
    };

    const completedCount = Object.values(items).filter(v => v === true).length;
    const progressPercent = (completedCount / Object.keys(items).length) * 100;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>FAA Required Checks</span>
          <span style={{ fontSize: 11, color: '#64748b' }}>{completedCount}/9 complete</span>
        </div>
        <div style={{ background: '#334155', borderRadius: 10, height: 6, marginBottom: 16 }}>
          <div style={{ background: '#22c55e', borderRadius: 10, width: `${progressPercent}%`, height: 6 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.entries(checklistLabels).map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={items[key] || false} onChange={() => handleToggle(key)} style={{ width: 18, height: 18, borderRadius: 6 }} />
              <span style={{ fontSize: 12, color: '#cbd5e1', flex: 1 }}>{label}</span>
            </label>
          ))}
        </div>
        {saving && <p style={{ fontSize: 10, color: '#475569', textAlign: 'center', marginTop: 12 }}>Saving...</p>}
      </div>
    );
  };

  const pdfData = {
    departure_icao: departureIcao,
    destination_icao: destinationIcao,
    trip_distance_nm: flightPlan?.calculated_trip_distance,
    calculated_trip_fuel_gallons: flightPlan?.calculated_trip_fuel_gallons,
    calculated_reserve_fuel_gallons: flightPlan?.calculated_reserve_fuel_gallons,
    calculated_total_fuel_required_gallons: flightPlan?.calculated_total_fuel_required_gallons,
    passenger_count: flightPlan?.passenger_count || 1,
    baggage_weight_lbs: flightPlan?.baggage_weight_lbs || 0,
    estimated_taxi_fuel_gallons: flightPlan?.estimated_taxi_fuel_gallons || 2,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0f172a', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '20px 16px', borderBottom: '0.5px solid #334155', background: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>Fuel Request #{request.id}</h3>
          <span style={{ fontSize: 12, color: getStatusColor(request.status), marginTop: 4, display: 'inline-block' }}>{request.status.toUpperCase()}</span>
        </div>
        <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <X size={18} color="#94a3b8" />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', padding: '12px 16px', gap: 8, borderBottom: '0.5px solid #334155', background: '#0f172a' }}>
        {[
          { id: 'fuel', label: 'Fuel Status', icon: Fuel },
          { id: 'flight', label: 'Flight Plan', icon: Navigation },
          { id: 'checklist', label: 'Checklist', icon: Shield }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: '8px 0', borderRadius: 20, border: 'none',
              background: activeTab === tab.id ? '#3b82f6' : '#1e293b',
              color: activeTab === tab.id ? '#fff' : '#94a3b8',
              fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {activeTab === 'fuel' && (
          <>
            <StatusTimeline />
            {aircraft && (
              <FuelRequirementCalculator
                aircraft={aircraft}
                requestId={request.id}
                departureIcao={departureIcao}
                destinationIcao={destinationIcao}
                onCalculationComplete={() => loadFlightPlan()}
              />
            )}
          </>
        )}
        {activeTab === 'flight' && <SkyVectorLinks />}
        {activeTab === 'checklist' && <FAAChecklist />}
      </div>

      {/* Footer Actions */}
      <div style={{ padding: '16px', borderTop: '0.5px solid #334155', gap: 12, display: 'flex', flexDirection: 'column' }}>
        <PDFDownloadLink
          document={<FlightPlanPDF flightPlan={pdfData} request={request} aircraft={aircraft} checklist={flightPlan?.checklist_items} companyLogo={companyLogo} companyName={companyName} />}
          fileName={`FlightPlan_${request.id}.pdf`}
        >
          {({ loading: pdfLoading }) => (
            <button style={{ width: '100%', padding: '14px', borderRadius: 16, background: '#3b82f6', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' }}>
              <Download size={16} /> {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
            </button>
          )}
        </PDFDownloadLink>
        {request.status === 'pending' && (
          <button onClick={async () => {
            if (window.confirm('Cancel this request?')) {
              await cancelFuelRequest(request.id);
              onRefresh();
              onClose();
            }
          }} style={{ width: '100%', padding: '14px', borderRadius: 16, background: 'rgba(239,68,68,0.15)', border: 'none', color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Cancel Request
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Formulaire ───────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 14, padding: '14px 14px', color: '#f1f5f9',
  fontSize: 16, outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
};

// ─── Composant Principal ──────────────────────────────────────────────────────
const MobileFuelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [aircraftList, setAircraftList] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [form, setForm] = useState({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  const loadRequests = useCallback(async (animate = false) => {
    if (animate) setRefreshing(true); else setLoading(true);
    try {
      const r = await getFuelRequests();
      setRequests(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    loadRequests();
    getMyAircraft().then(r => setAircraftList(r.data)).catch(() => {});
    intervalRef.current = setInterval(() => loadRequests(), 60000);
    return () => clearInterval(intervalRef.current);
  }, [loadRequests]);

  const handleSubmit = async () => {
    if (!form.aircraft_id || !form.quantity || !form.scheduled_date) {
      alert('Please fill in all required fields'); return;
    }
    setSubmitting(true);
    try {
      await createFuelRequest(form);
      setShowSheet(false);
      setForm({ aircraft_id: '', quantity: '', scheduled_date: '', description: '' });
      await loadRequests();
    } catch { alert('Error creating request'); }
    finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await cancelFuelRequest(id); await loadRequests(); }
    catch { alert('Error cancelling request'); }
  };

  const openDrawer = (request) => {
    const aircraft = aircraftList.find(a => a.id === request.aircraft_id);
    if (aircraft) {
      setSelectedAircraft(aircraft);
      setSelectedRequest(request);
    }
  };

  const formatDate = (s) => {
    const d = new Date(s);
    return d.toLocaleDateString('en-US') + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ padding: '0 16px 100px' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#475569' }}>Auto-refresh · 60s</span>
        <button onClick={() => loadRequests(true)} style={{ background: '#1e293b', border: '0.5px solid #334155', borderRadius: 10, padding: '8px 10px', cursor: 'pointer' }}>
          <RefreshCw size={15} color={refreshing ? '#3b82f6' : '#94a3b8'} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <Fuel size={40} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569', marginBottom: 16 }}>No fuel requests</p>
          <button onClick={() => setShowSheet(true)} style={{ background: '#3b82f6', border: 'none', borderRadius: 12, padding: '10px 20px', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Create a request
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map((req) => (
            <div key={req.id} onClick={() => openDrawer(req)} style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', padding: '14px 16px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Fuel size={18} color="#fb923c" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{req.quantity} gal</span>
                    <Badge status={req.status} />
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {req.tail_number} – {req.manufacturer} {req.model}
                  </p>
                  {req.destination_icao && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      <MapPin size={10} color="#64748b" />
                      <span style={{ fontSize: 11, color: '#475569' }}>{req.destination_icao}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={11} color="#475569" />
                    <span style={{ fontSize: 11, color: '#475569' }}>{formatDate(req.scheduled_date)}</span>
                  </div>
                </div>
                <ChevronRight size={16} color="#64748b" style={{ flexShrink: 0 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
      <button onClick={() => setShowSheet(true)} style={{
        position: 'fixed', bottom: 'calc(72px + env(safe-area-inset-bottom))', right: 20,
        width: 56, height: 56, borderRadius: 28, background: '#3b82f6', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 4px 20px rgba(59,130,246,0.45)', zIndex: 50,
      }}>
        <Plus size={24} color="#fff" />
      </button>

      {/* Bottom Sheet Form */}
      <BottomSheet open={showSheet} onClose={() => setShowSheet(false)} title="New fuel request">
        <Field label="Aircraft *">
          <select value={form.aircraft_id} onChange={e => setForm({ ...form, aircraft_id: e.target.value })} style={{ ...inputStyle, appearance: 'none' }}>
            <option value="">Select an aircraft</option>
            {aircraftList.map(a => <option key={a.id} value={a.id}>{a.tail_number} – {a.manufacturer} {a.model}</option>)}
          </select>
        </Field>
        <Field label="Destination ICAO (optional)">
          <input type="text" placeholder="e.g., KDFW" value={form.destination_icao || ''} onChange={e => setForm({ ...form, destination_icao: e.target.value.toUpperCase() })} style={inputStyle} />
        </Field>
        <Field label="Quantity (gallons) *">
          <input type="number" inputMode="numeric" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} style={inputStyle} placeholder="Ex: 150" />
        </Field>
        <Field label="Desired date and time *">
          <input type="datetime-local" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} style={inputStyle} />
        </Field>
        <Field label="Description (optional)">
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} placeholder="Additional information..." />
        </Field>
        <button onClick={handleSubmit} disabled={submitting} style={{
          width: '100%', padding: '16px', borderRadius: 16,
          background: submitting ? '#1e40af' : '#3b82f6', border: 'none',
          color: '#fff', fontSize: 16, fontWeight: 600,
          cursor: submitting ? 'default' : 'pointer', marginTop: 8,
        }}>
          {submitting ? 'Submitting...' : 'Submit request'}
        </button>
      </BottomSheet>

      {/* Drawer */}
      {selectedRequest && selectedAircraft && (
        <FuelRequestDrawerMobile
          request={selectedRequest}
          aircraft={selectedAircraft}
          onClose={() => { setSelectedRequest(null); setSelectedAircraft(null); }}
          onRefresh={loadRequests}
        />
      )}
    </div>
  );
};

export default MobileFuelRequests;